/**
 * A cheap, non-authoritative "this browser has a session" marker.
 *
 * ## The problem it solves
 *
 * The access token lives in memory only (SEC-1) and the refresh token is the httpOnly `dz_refresh`
 * cookie, which JS cannot read. So on a HARD page load there is no client-side evidence of a
 * session at all — the only way to find out is to spend `GET /auth/me`, and when nobody is signed in
 * that is a guaranteed 401 followed by a guaranteed 401 from the refresh the interceptor then tries.
 *
 * Two failed requests, on every visit, by every anonymous visitor. On the public funnel — `/website`
 * → `/rounds/{id}` → `/apply/{id}/register` — anonymous IS the normal case, so that is the wrong
 * default. Measured on the real app before this existed.
 *
 * An in-memory check cannot replace it either: a signed-in applicant browsing the public pages makes
 * no authenticated request, so their token is empty and they look exactly like a stranger. Verified
 * the hard way — the shortcut it was supposed to enable never fired.
 *
 * ## What this is
 *
 * One bit, written when a profile is seeded (login, signup-then-login) and removed when it is
 * cleared (logout, session loss). It says **"asking is worth it"**, nothing more:
 *
 * - `false` → nobody has ever signed in in this browser, so skip the probe entirely.
 * - `true`  → probe. It may still fail, and that is fine: the marker is self-correcting, because the
 *             failure path clears the profile and with it this bit.
 *
 * ## What it is NOT
 *
 * **Not authentication, and not usable as a guard.** It is a plain `localStorage` string the user can
 * set by hand, it survives a session revoked on another device, and it says nothing about who the
 * user is or what they may see. Every access decision still goes through `tryGetProfile` and the
 * server. Being wrong costs one wasted request or one skipped shortcut — never access.
 *
 * It stores no identity: no email, no name, no token, no id. Just the fact that a session once
 * existed here.
 */
const KEY = "dzc.session-hint.v1";

/** Is a probe worth making? `false` means "definitely never signed in here". */
export function hasSessionHint(): boolean {
  try {
    return window.localStorage.getItem(KEY) === "1";
  } catch {
    // Storage blocked (private window, site data off). Assume a session MIGHT exist: paying a
    // wasted probe is a smaller failure than silently withholding the signed-in path.
    return true;
  }
}

/** Record that a session exists in this browser. */
export function setSessionHint(): void {
  try {
    window.localStorage.setItem(KEY, "1");
  } catch {
    // Nothing to do; `hasSessionHint` fails open, so behaviour stays correct without it.
  }
}

/** Forget it — logout, or a refresh that could not be recovered. */
export function clearSessionHint(): void {
  try {
    window.localStorage.removeItem(KEY);
  } catch {
    // Same as above. A stale marker only ever costs one failed probe.
  }
}
